import { useState } from "react";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsTrigger,
  TabsContent,
  TabsListWithIndicator,
} from "@/core/components/ui/tabs";
import { toast } from "@/core/components/ui/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { ReferralList } from "@/business-settings/common/referral-list";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { NoteList } from "@/business-settings/customers/common/note-list";
import { PayoutList } from "@/business-settings/customers/common/payout-list";
import { DetailsTab } from "@/business-settings/customers/details";
import { formatPhoneNumber } from "@/core/utils/phone";
import {
  useGetAdvocate,
  useGetCustomerNotes,
  useOptOutCustomer,
} from "@/customer/query";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwner } from "@/user/utils";
import { AdvocateSidebarSkeleton } from "./advocate-sidebar-skeleton";

interface AdvocateSidebarProps {
  businessId: string;
  mode: "view" | "new";
  advocateId: string | undefined;
}

const AdvocateSidebar = ({
  businessId,
  mode,
  advocateId,
}: AdvocateSidebarProps) => {
  const { data: advocate = null, isLoading } = useGetAdvocate(advocateId);

  const [activeTab, setActiveTab] = useState("details");
  const { resetSidebar } = useBusinessSettingsSidebar();
  const isNewMode = mode === "new";
  const { data: user } = useGetMyUser();
  const optOutCustomer = useOptOutCustomer(businessId);

  const customerId = advocateId || "";
  const { data: notes = [] } = useGetCustomerNotes(customerId);

  const canChangeAttribution = isAdminOrOwner(user?.role);
  const { referrals = [], payouts = [] } = advocate || {};

  const headerText = "Advocates";
  const name =
    advocate?.name ||
    formatPhoneNumber(advocate?.phone_number) ||
    "New Advocate";

  const handleOptOut = () => {
    if (advocateId) {
      optOutCustomer.mutate(advocateId, {
        onSuccess: () => {
          toast.success("Customer opted out from messaging");
          resetSidebar();
        },
        onError: () => {
          toast.error("Failed to opt out customer from messaging");
        },
      });
    }
  };

  const dropdownActions =
    mode === "view" && advocateId
      ? [
          {
            label: "Opt out from messaging",
            onClick: handleOptOut,
            variant: "destructive" as const,
          },
        ]
      : undefined;

  if (isLoading) {
    return <AdvocateSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
        dropdownActions={dropdownActions}
      />
      <div className="flex-1 overflow-hidden">
        <Stack gap="none" fullWidth className="h-full">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex w-full flex-1 flex-col overflow-hidden px-6"
          >
            <TabsListWithIndicator
              containerClassName="w-full font-inter !font-medium px-1"
              className="w-full justify-center pb-0"
              size="small"
              hideBottomBorder={true}
            >
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
              <TabsTrigger value="referrals">
                Referrals ({referrals.length})
              </TabsTrigger>
              <TabsTrigger value="payouts">
                Payouts ({payouts.length})
              </TabsTrigger>
            </TabsListWithIndicator>

            <TabsContent value="details" className="pt-4">
              <DetailsTab
                businessId={businessId}
                initialData={advocate}
                isNewMode={isNewMode}
                canChangeAttribution={canChangeAttribution}
              />
            </TabsContent>

            <TabsContent value="notes">
              <NoteList notes={notes} customerId={advocateId} />
            </TabsContent>

            <TabsContent value="referrals">
              <ReferralList
                referrals={referrals}
                isLoading={false}
                hideHeader
              />
            </TabsContent>

            <TabsContent value="payouts">
              <PayoutList payouts={payouts} />
            </TabsContent>
          </Tabs>
        </Stack>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default AdvocateSidebar;
